import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';
import { MosyUser } from '../types';

export interface Comment {
  id: number;
  asset: number | null;
  task: string | null;
  author: number | null;
  author_details: MosyUser | null;
  mention_details: MosyUser[];
  body: string;
  created_at: string;
  updated_at: string;
}

export type CommentParent =
  | { assetId: number; taskId?: undefined }
  | { taskId: string; assetId?: undefined };

const listKeyForAsset = (assetId: number) => ['comments', { asset: assetId }] as const;
const listKeyForTask = (taskId: string) => ['comments', { task: taskId }] as const;

export const useComments = (parent: CommentParent | null | undefined) =>
  useQuery({
    queryKey: parent
      ? (parent.assetId != null ? listKeyForAsset(parent.assetId) : listKeyForTask(parent.taskId!))
      : ['comments', { noop: true }],
    enabled: !!parent,
    queryFn: async () => {
      const param = parent && parent.assetId != null
        ? `asset=${parent.assetId}`
        : `task=${encodeURIComponent(parent!.taskId!)}`;
      const res = await axios.get<Comment[]>(`api/comments/?${param}`);
      return res.data;
    },
  });

export const useCreateComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string } & ({ asset: number } | { task: string })) => {
      const res = await axios.post<Comment>('api/comments/', input);
      return res.data;
    },
    onSuccess: (created) => {
      if (created.asset != null) qc.invalidateQueries({ queryKey: listKeyForAsset(created.asset) });
      else if (created.task != null) qc.invalidateQueries({ queryKey: listKeyForTask(created.task) });
    },
  });
};

export const useDeleteComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: number; parent: CommentParent }) => {
      await axios.delete(`api/comments/${id}/`);
    },
    onSuccess: (_void, { parent }) => {
      if (parent.assetId != null) qc.invalidateQueries({ queryKey: listKeyForAsset(parent.assetId) });
      else qc.invalidateQueries({ queryKey: listKeyForTask(parent.taskId!) });
    },
  });
};
